import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/format'

export type TimelineDot = 'navy' | 'gold' | 'green' | 'red' | 'muted'

export interface TimelineItem {
  id: string | number
  /** Data zdarzenia (ISO albo Date) — renderowana w lewej kolumnie. */
  date: string | Date | null | undefined
  title: ReactNode
  description?: ReactNode
  dot?: TimelineDot
  /** Dodatkowa treść pod opisem — np. chipy z załącznikami. */
  children?: ReactNode
  onClick?: () => void
}

export interface TimelineProps {
  items: TimelineItem[]
  className?: string
}

const DOTS: Record<TimelineDot, string> = {
  navy: 'bg-navy-600',
  gold: 'bg-gold',
  green: 'bg-success',
  red: 'bg-danger',
  muted: 'bg-[#b7c3cc]',
}

/** Oś czasu „Historia sprawy": data po lewej, kropka na linii, treść po prawej. */
export function Timeline({ items, className }: TimelineProps) {
  return (
    <ol className={cn('relative', className)}>
      {items.map((item, index) => (
        <li key={item.id} className="relative grid grid-cols-[84px_1fr] gap-x-4">
          <div className="pt-[1px] text-right text-small tabular-nums text-muted">{formatDate(item.date)}</div>
          <div
            className={cn(
              'relative border-l border-line-soft pb-6 pl-5',
              index === items.length - 1 && 'border-l-transparent pb-1',
            )}
          >
            <span
              className={cn(
                'absolute -left-[4.5px] top-[5px] h-[9px] w-[9px] rounded-full ring-2 ring-card',
                DOTS[item.dot ?? 'navy'],
              )}
            />
            <div
              onClick={item.onClick}
              className={cn('min-w-0', item.onClick && 'cursor-pointer')}
            >
              <p className="text-base font-semibold leading-snug text-ink">{item.title}</p>
              {item.description ? <p className="mt-1 text-small text-muted">{item.description}</p> : null}
              {item.children ? <div className="mt-2.5 flex flex-wrap gap-2">{item.children}</div> : null}
            </div>
          </div>
        </li>
      ))}
    </ol>
  )
}

/**
 * Chip z nazwą załącznika pod wpisem osi czasu.
 *
 * ŚWIADOMIE NIEINTERAKTYWNY: `case_events` trzyma samą NAZWĘ pliku (`attachment_name`),
 * a nie referencję do dokumentu — nie ma czego otworzyć. Chip był wcześniej `<button>`
 * z hoverem i bez `onClick`, więc obiecywał klik, którego nie da się obsłużyć.
 * Gdy encja zdarzenia dostanie `document_id`, w tym miejscu wraca `<Link>` do pisma.
 */
export function TimelineAttachment({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-[26px] items-center gap-2 rounded border border-line-soft bg-card px-2.5 text-small text-ink">
      <svg aria-hidden viewBox="0 0 12 14" className="h-[13px] w-[11px] text-[#93a5b1]">
        <path d="M1 .5h6.5L11 4v9.5H1z" fill="none" stroke="currentColor" strokeWidth="1" />
      </svg>
      {children}
    </span>
  )
}
